import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, AlertTriangle, Heart } from 'lucide-react';
import { essentialOils, carrierOils, herbs } from '../data/mock';
import { useKit } from '../hooks/useKit';
import { useProfiles } from '../contexts/ProfileContext';
import ItemPhoto from '../components/ItemPhoto';
import { warmupLibrary } from '../utils/photos';

const tabs = [
  { key: 'oils', label: 'Essential Oils', data: essentialOils, category: 'Essential Oil' },
  { key: 'carrier', label: 'Carrier Oils', data: carrierOils, category: 'Carrier Oil' },
  { key: 'herbs', label: 'Herbs', data: herbs, category: 'Herb' }
];

export default function Library() {
  const [tab, setTab] = useState('oils');
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { isSaved, toggle } = useKit();
  const { activeProfile, filterLibrary } = useProfiles();

  useEffect(() => {
    warmupLibrary();
  }, []);

  const active = tabs.find((t) => t.key === tab);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = !q
      ? active.data
      : active.data.filter(
          (i) =>
            i.name.toLowerCase().includes(q) ||
            i.latin.toLowerCase().includes(q) ||
            i.benefits.join(' ').toLowerCase().includes(q)
        );
    return filterLibrary(base);
  }, [active, query, filterLibrary]);

  return (
    <div className="max-w-6xl mx-auto px-5 py-10 min-h-[70vh]">
      <h1 className="font-serif text-4xl text-botanica-ink mb-2">Library</h1>
      {activeProfile ? (
        <p className="text-sm text-botanica-muted mb-6">
          Showing only what's safe for <span className="text-botanica-greenDark font-medium">{activeProfile.first_name}</span>. Switch profiles from the menu, top right.
        </p>
      ) : (
        <p className="text-sm text-botanica-muted mb-6">
          Tip: <Link to="/profile" className="text-botanica-green underline">create a profile</Link> to hide anything unsafe for you.
        </p>
      )}

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-botanica-muted" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or benefit..."
          className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-botanica-card/70 border border-botanica-card text-sm text-botanica-ink placeholder:text-botanica-muted focus:outline-none focus:ring-2 focus:ring-botanica-green/40"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-7">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${
              tab === t.key
                ? 'bg-botanica-green text-white font-medium'
                : 'bg-botanica-card/60 text-botanica-muted hover:text-botanica-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-botanica-muted py-16 text-center">No results for “{query}”. Try another search.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {filtered.map((item) => {
            const saved = isSaved(item.id);
            const warn = item.level === 'caution';
            return (
              <button
                key={item.id}
                onClick={() => navigate(`/library/${item.id}`)}
                className="group text-left bg-botanica-card/70 hover:bg-botanica-card rounded-2xl p-4 border border-transparent hover:border-botanica-green/20 transition-all hover:-translate-y-0.5 flex gap-4"
              >
                <ItemPhoto itemId={item.id} alt={item.name} className="w-24 h-24 rounded-xl shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-serif text-lg text-botanica-ink leading-tight truncate">{item.name}</h3>
                      <p className="italic text-xs text-botanica-muted mt-0.5 truncate">{item.latin}</p>
                    </div>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle({ id: item.id, name: item.name, type: 'library', category: active.category, subtitle: item.latin });
                      }}
                      className="p-1.5 rounded-full hover:bg-botanica-panel transition-colors shrink-0"
                    >
                      <Heart className={`w-4 h-4 ${saved ? 'fill-botanica-green text-botanica-green' : 'text-botanica-muted'}`} />
                    </span>
                  </div>
                  <p className="text-sm text-botanica-ink/80 mt-1.5 line-clamp-1">{item.benefits.join(' • ')}</p>
                  <p className={`text-xs mt-1.5 flex items-center gap-1.5 ${warn ? 'text-botanica-warn' : 'text-botanica-green'}`}>
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span className="line-clamp-1">{item.safety}</span>
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
